import { Shield, FileText, Mail, ArrowLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { getSupportEmail, openMailTo } from '../utils/obfuscate';

export function TermsPage({ onBack }) {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-[#0f1419] pt-24 pb-16">
      <div className="max-w-3xl mx-auto px-4 sm:px-6">
        {onBack && (
          <button onClick={onBack} className="text-slate-400 hover:text-white mb-6 flex items-center gap-2">
            <ArrowLeft className="w-4 h-4" /> {t('common.back')}
          </button>
        )}
        <div className="flex items-center gap-3 mb-8">
          <FileText className="w-8 h-8 text-teal-400" />
          <h1 className="text-3xl font-bold text-white font-outfit" data-testid="terms-title">{t('legal.terms.title')}</h1>
        </div>
        <div className="prose prose-invert prose-sm max-w-none space-y-6 text-slate-300">
          <p className="text-slate-400 text-sm">{t('legal.lastUpdated')}</p>

          {['s1', 's2', 's3', 's4', 's5'].map(key => (
            <section key={key} className="bg-white/5 border border-white/10 rounded-xl p-6">
              <h2 className="text-lg font-semibold text-white mb-3">{t(`legal.terms.${key}.title`)}</h2>
              <p className="text-sm leading-relaxed">{t(`legal.terms.${key}.text`)}</p>
            </section>
          ))}

          <section className="bg-white/5 border border-white/10 rounded-xl p-6">
            <h2 className="text-lg font-semibold text-white mb-3">{t('legal.terms.s6.title')}</h2>
            <p className="text-sm leading-relaxed">{t('legal.terms.s6.text')}</p>
            <ul className="text-sm mt-2 space-y-1 list-disc list-inside text-slate-400">
              {(t('legal.terms.s6.items', { returnObjects: true }) || []).map((item, i) => (
                <li key={i}>{item}</li>
              ))}
            </ul>
            <p className="text-sm mt-2">{t('legal.terms.s6.refundContact')} <span className="text-teal-400 cursor-pointer hover:underline" onClick={openMailTo}>{getSupportEmail()}</span></p>
          </section>

          {['s7', 's8'].map(key => (
            <section key={key} className="bg-white/5 border border-white/10 rounded-xl p-6">
              <h2 className="text-lg font-semibold text-white mb-3">{t(`legal.terms.${key}.title`)}</h2>
              <p className="text-sm leading-relaxed">{t(`legal.terms.${key}.text`)}</p>
            </section>
          ))}

          <section className="bg-white/5 border border-white/10 rounded-xl p-6">
            <h2 className="text-lg font-semibold text-white mb-3">{t('legal.terms.s9.title')}</h2>
            <p className="text-sm leading-relaxed">
              {t('legal.terms.s9.text')}<br/>
              Email: <span className="cursor-pointer hover:underline text-teal-400" onClick={openMailTo}>{getSupportEmail()}</span><br/>
              Website: esim4you.com
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}

export function PrivacyPage({ onBack }) {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-[#0f1419] pt-24 pb-16">
      <div className="max-w-3xl mx-auto px-4 sm:px-6">
        {onBack && (
          <button onClick={onBack} className="text-slate-400 hover:text-white mb-6 flex items-center gap-2">
            <ArrowLeft className="w-4 h-4" /> {t('common.back')}
          </button>
        )}
        <div className="flex items-center gap-3 mb-8">
          <Shield className="w-8 h-8 text-teal-400" />
          <h1 className="text-3xl font-bold text-white font-outfit" data-testid="privacy-title">{t('legal.privacy.title')}</h1>
        </div>
        <div className="prose prose-invert prose-sm max-w-none space-y-6 text-slate-300">
          <p className="text-slate-400 text-sm">{t('legal.lastUpdated')}</p>

          <section className="bg-white/5 border border-white/10 rounded-xl p-6">
            <h2 className="text-lg font-semibold text-white mb-3">{t('legal.privacy.s1.title')}</h2>
            <p className="text-sm leading-relaxed">{t('legal.privacy.s1.text')}</p>
          </section>

          {['s2', 's3', 's4', 's5'].map(key => (
            <section key={key} className="bg-white/5 border border-white/10 rounded-xl p-6">
              <h2 className="text-lg font-semibold text-white mb-3">{t(`legal.privacy.${key}.title`)}</h2>
              <ul className="text-sm space-y-2 list-disc list-inside text-slate-400">
                {(t(`legal.privacy.${key}.items`, { returnObjects: true }) || []).map((item, i) => (
                  <li key={i}>{item}</li>
                ))}
              </ul>
              {t(`legal.privacy.${key}.note`, '') && (
                <p className="text-sm mt-2">{t(`legal.privacy.${key}.note`)}</p>
              )}
            </section>
          ))}

          <section className="bg-white/5 border border-white/10 rounded-xl p-6">
            <h2 className="text-lg font-semibold text-white mb-3">{t('legal.privacy.s6.title')}</h2>
            <p className="text-sm mb-2">{t('legal.privacy.s6.intro')}</p>
            <ul className="text-sm space-y-1 list-disc list-inside text-slate-400">
              {(t('legal.privacy.s6.items', { returnObjects: true }) || []).map((item, i) => (
                <li key={i}>{item}</li>
              ))}
            </ul>
            <p className="text-sm mt-2">{t('legal.privacy.s6.contact')} <strong className="text-teal-400">privacy@esim4you.com</strong></p>
          </section>

          {['s7', 's8'].map(key => (
            <section key={key} className="bg-white/5 border border-white/10 rounded-xl p-6">
              <h2 className="text-lg font-semibold text-white mb-3">{t(`legal.privacy.${key}.title`)}</h2>
              <p className="text-sm leading-relaxed">{t(`legal.privacy.${key}.text`)}</p>
            </section>
          ))}

          <section className="bg-white/5 border border-white/10 rounded-xl p-6">
            <h2 className="text-lg font-semibold text-white mb-3">{t('legal.privacy.s9.title')}</h2>
            <p className="text-sm leading-relaxed whitespace-pre-line">{t('legal.privacy.s9.text')}</p>
          </section>
        </div>
      </div>
    </div>
  );
}

export function RefundPage({ onBack }) {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-[#0f1419] pt-24 pb-16">
      <div className="max-w-3xl mx-auto px-4 sm:px-6">
        {onBack && (
          <button onClick={onBack} className="text-slate-400 hover:text-white mb-6 flex items-center gap-2">
            <ArrowLeft className="w-4 h-4" /> {t('common.back')}
          </button>
        )}
        <div className="flex items-center gap-3 mb-8">
          <Mail className="w-8 h-8 text-teal-400" />
          <h1 className="text-3xl font-bold text-white font-outfit" data-testid="refund-title">{t('legal.refund.title')}</h1>
        </div>
        <div className="prose prose-invert prose-sm max-w-none space-y-6 text-slate-300">
          <section className="bg-white/5 border border-white/10 rounded-xl p-6">
            <h2 className="text-lg font-semibold text-white mb-3">{t('legal.refund.beforeActivation.title')}</h2>
            <p className="text-sm leading-relaxed">{t('legal.refund.beforeActivation.text')}</p>
          </section>

          <section className="bg-white/5 border border-white/10 rounded-xl p-6">
            <h2 className="text-lg font-semibold text-white mb-3">{t('legal.refund.afterActivation.title')}</h2>
            <p className="text-sm leading-relaxed">{t('legal.refund.afterActivation.text')}</p>
          </section>

          <section className="bg-white/5 border border-white/10 rounded-xl p-6">
            <h2 className="text-lg font-semibold text-white mb-3">{t('legal.refund.technicalIssues.title')}</h2>
            <p className="text-sm leading-relaxed">{t('legal.refund.technicalIssues.text')}</p>
            <ul className="text-sm mt-2 space-y-1 list-disc list-inside text-slate-400">
              {(t('legal.refund.technicalIssues.items', { returnObjects: true }) || []).map((item, i) => (
                <li key={i}>{item}</li>
              ))}
            </ul>
          </section>

          <section className="bg-white/5 border border-white/10 rounded-xl p-6">
            <h2 className="text-lg font-semibold text-white mb-3">{t('legal.refund.howToRequest.title')}</h2>
            <p className="text-sm leading-relaxed">{t('legal.refund.howToRequest.text')} <strong className="text-teal-400 cursor-pointer hover:underline" onClick={openMailTo}>{getSupportEmail()}</strong> {t('legal.refund.howToRequest.with')}</p>
            <ul className="text-sm mt-2 space-y-1 list-disc list-inside text-slate-400">
              {(t('legal.refund.howToRequest.items', { returnObjects: true }) || []).map((item, i) => (
                <li key={i}>{item}</li>
              ))}
            </ul>
            <p className="text-sm mt-2">{t('legal.refund.howToRequest.processing')}</p>
          </section>
        </div>
      </div>
    </div>
  );
}
